import { Injectable } from '@angular/core';
import { ILatLng } from 'src/app/classes/def/map/coords';
import { IVirtualLocation, EVirtualLocationSource } from '../app/modules/virtual-position';
import { VirtualPositionService } from '../app/modules/virtual-position';
import { MapManagerService } from './map-manager';
import { LocationMonitorService } from './location-monitor';
import { IGeolocationResult } from 'src/app/classes/def/map/map-data';


@Injectable({ providedIn: 'root' })
export class TourSimulatorService {
    private speedMps = 14; // 10x walking speed
    private updateIntervalMs = 500;
    private isRunning = false;
    private isPaused = false;

    constructor(
        private virtualPosition: VirtualPositionService,
        private mapManager: MapManagerService,
        private locationMonitor: LocationMonitorService
    ) { }

    async startSegment(
        path: ILatLng[],
        checkpointIndex: number,
        onReached: (point: ILatLng, index: number) => void
    ): Promise<void> {
        if (!path || path.length < 2) return;

        const distancePerStep = this.speedMps * (this.updateIntervalMs / 1000); // ~0.7 meters per step

        this.isRunning = true;
        this.isPaused = false;

        // Precompute segment distances
        const distances: number[] = [];
        let totalDistance = 0;
        for (let i = 0; i < path.length - 1; i++) {
            const d = this.getDistanceMeters(path[i], path[i + 1]);
            distances.push(d);
            totalDistance += d;
        }

        let distanceTraveled = 0;
        let segmentIndex = 0;
        let segmentProgress = 0;

        while (this.isRunning && distanceTraveled < totalDistance) {
            while (this.isPaused) await this.sleep(100);
            if (segmentIndex >= distances.length) break;

            const segDistance = distances[segmentIndex];
            const progress = segmentProgress / segDistance;
            const from = path[segmentIndex];
            const to = path[segmentIndex + 1];

            const interpolated = this.interpolateLatLng(from, to, progress);
            const heading = this.calculateHeading(from, to);

            const loc: IVirtualLocation = {
                coords: interpolated,
                speed: this.speedMps,
                heading,
                source: EVirtualLocationSource.simulator,
                updateMarker: true
            };

            const gloc: IGeolocationResult = {
                coords: interpolated,
                timestamp: Date.now(),
                elapsed: 0,
                heading,
                accuracy: 100,
                altitude: 0,
                speed: this.speedMps
            };

            await this.mapManager.moveMapExt(loc.coords);
            this.virtualPosition.updateVirtualPosition(loc);
            this.locationMonitor.setLocationFromUnified(gloc);

            // advance distance
            segmentProgress += distancePerStep;
            distanceTraveled += distancePerStep;

            if (segmentProgress >= segDistance) {
                segmentIndex++;
                segmentProgress = 0;
            }

            await this.sleep(this.updateIntervalMs);
        }

        this.isRunning = false;
        onReached(path[path.length - 1], checkpointIndex);
    }


    pause(): void {
        if (!this.isRunning) return;
        this.isPaused = true;
    }

    resume(): void {
        if (this.isRunning && this.isPaused) {
            this.isPaused = false;
        }
    }

    stop(): void {
        this.isRunning = false;
        this.isPaused = false;
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private calculateHeading(from: ILatLng, to: ILatLng): number {
        const lat1 = this.deg2rad(from.lat);
        const lat2 = this.deg2rad(to.lat);
        const dLng = this.deg2rad(to.lng - from.lng);

        const y = Math.sin(dLng) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
        const brng = Math.atan2(y, x);
        return (this.rad2deg(brng) + 360) % 360;
    }

    private deg2rad(deg: number): number {
        return deg * (Math.PI / 180);
    }

    private rad2deg(rad: number): number {
        return rad * (180 / Math.PI);
    }

    private interpolateLatLng(a: ILatLng, b: ILatLng, t: number): ILatLng {
        return {
            lat: a.lat + (b.lat - a.lat) * t,
            lng: a.lng + (b.lng - a.lng) * t
        };
    }

    private getDistanceMeters(a: ILatLng, b: ILatLng): number {
        const R = 6371000;
        const dLat = this.deg2rad(b.lat - a.lat);
        const dLng = this.deg2rad(b.lng - a.lng);
        const lat1 = this.deg2rad(a.lat);
        const lat2 = this.deg2rad(b.lat);

        const aVal = Math.sin(dLat / 2) ** 2 +
            Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
        const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
        return R * c;
    }

}

